import React, { useState, useEffect, useContext } from "react";
import { Container, Row, Col, Form, Button, Spinner } from "react-bootstrap";
import BreadcrumbComp from "../layouts/BreadcrumbComp";
import { AuthContext } from "../../contexts/AuthContext";
import { useDispatch } from "react-redux";
import { menuActions } from "../../store/menu-slice";
import { exportToExcel } from "../utils/ExcelCreation";
import errorImage from "../../icons/error-tick-logo.svg";
import { getDivisions } from '../../services/user-service';
import { getAllLocationForDivision } from "../../services/location-master-service";
import { formatDateInDDMMYYYY, RenderIf } from "../../functions/common-functions";
import { BiSelectMultiple } from "react-icons/bi";
import SingleValuePopUp from "../utils/SingleValuePopUp";
import { Multiselect } from 'multiselect-react-dropdown';
import '../reports/MultiSelectStyles.css';
import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";
import SearchSelect from "../common/SearchSelect";
import { getAllUOM } from "../../services/disposal-intimation-service";
import { getUserFrozenBids } from "../../services/maintain-bid-service";
import { addLogDetails } from "../../services/logger-entry-service";

const ClosedBids = () => {
  const MySwal = withReactContent(Swal);
  const context = useContext(AuthContext);
  const dispatch = useDispatch();
  dispatch(menuActions.setMenu("Closed Bids"));
  const [loading, setLoading] = useState(false);

  const [filters, setFilters] = useState({
    enquiryNumber: '',
    enquiryStatus: [],
    enquirySource: '',
    enquirySourceNumber: '',
    itemCode: '',
    itemDetail: '',
    itemType: '',
    division: '',
    location: '',
    quantity: '',
    uom: '',
    gst: '',
    lnCompany: '',
    dueDate: '',
    price: '',
    bidSubmissionDate: '',
    revisedDueDate: '',
    salesOrderNumber: '',
    remarks: ''
  });

  const [filterData, setFilterData] = useState({
    enquiryNumbers: [],
    enquiryStatuses: [],
    enquirySourceNumbers: [],
    enquirySources: ['Contract', 'Non-Contract'],
    itemTypes: [],
    divisions: [],
    locations: [],
    uoms: [],
    itemCodes: [],
    salesOrderNumbers: [],
    itemDetails: [],
    quantities: [],
    remarks: []
  });

  // Popup states
  const [showForm, setShowForm] = useState(false);
  const [popUp, setPopUp] = useState({
    name: '',
    type: '',
    key: '',
    value: ''
  });

  // Autocomplete states - using object pattern like EnquiryTenderReports
  const [suggestions, setSuggestions] = useState({});
  const [showSuggestions, setShowSuggestions] = useState({});
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchFilterData();
  }, []);

  const fetchFilterData = async () => {
    setLoading(true);
    try {
      const [data, allDivisions, allUOM] = await Promise.all([
        getUserFrozenBids(context.userEmail),
        getDivisions(),
        getAllUOM()
      ]);

      if (!Array.isArray(data)) throw new Error("Failed to fetch closed bids");

      setFilterData(prev => ({
        ...prev,
        enquiryNumbers: [...new Set(data.map(item => item.EnquiryNumber))].filter(Boolean),
        enquirySourceNumbers: [...new Set(data.map(item => item.EnquirySourceNumber))].filter(Boolean),
        itemDetails: [...new Set(data.map(item => item.ItemDetail))].filter(Boolean),
        quantities: [...new Set(data.map(item => item.Quantity))].filter(Boolean),
        enquiryStatuses: ["Created", "Not Assigned", "Mail Not Sent", "Live", "Past Due Date", "Rejected", "Deleted", "Frozen"],
        divisions: allDivisions,
        itemCodes: [...new Set(data.map(item => item.ItemCode))].filter(Boolean),
        itemTypes: [...new Set(data.map(item => item.ItemType))].filter(Boolean),
        uoms: allUOM,
        salesOrderNumbers: [...new Set(data.map(item => item.SONumber))].filter(Boolean),
        locations: [...new Set(data.map(item => item.Location))].filter(Boolean),
        remarks: [...new Set(data.map(item => item.Remarks))].filter(Boolean)
      }));
    } catch (error) {
      console.error("Error fetching filter data:", error);
      MySwal.fire({ text: "Failed to load closed bids. Please refresh.", imageUrl: errorImage, customClass: "swal-error" });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    if (field === 'enquiryNumber' && value.length > 30) return;
    if (field === 'enquirySourceNumber' && value.length > 30) return;
    if (field === 'itemDetail' && value.length > 250) return;
    if (field === 'quantity' && value.length > 50) return;
    if (field === 'gst' && value.length > 5) return;
    if (field === 'price' && value.length > 20) return;
    if (field === 'salesOrderNumber' && value.length > 20) return;
    if (field === 'remarks' && value.length > 250) return;

    if ((field === 'enquiryNumber' || field === 'enquirySourceNumber') && value && !/^[a-zA-Z0-9\/\-]*$/.test(value)) return;
    if (field === 'itemDetail' && value && !/^[a-zA-Z0-9 \-.,&()]*$/.test(value)) return;
    if (field === 'quantity' && value && !/^[a-zA-Z0-9 ]*$/.test(value)) return;
    if (field === 'gst' && value && !/^\d*\.?\d{0,2}$/.test(value)) return;
    if (field === 'price' && value && !/^\d*\.?\d{0,2}$/.test(value)) return;
    if (field === 'salesOrderNumber' && value && !/^[a-zA-Z0-9]*$/.test(value)) return;
    if (field === 'remarks' && value && !/^[a-zA-Z0-9 \-.,()\[\]]*$/.test(value)) return;

    if (field === 'gst' && value && parseFloat(value) > 100) return;

    setFilters(prev => ({ ...prev, [field]: value }));

    if (field === 'division' && value) {
      fetchLocationsByDivision(value);
      setFilters(prev => ({ ...prev, location: '' }));
    }

    if (field === 'enquiryNumber' && filterData.enquiryNumbers) {
      const filtered = filterData.enquiryNumbers.filter(num => num.toLowerCase().includes(value.toLowerCase()));
      setSuggestions(prev => ({ ...prev, enquiryNumber: filtered }));
      setShowSuggestions(prev => ({ ...prev, enquiryNumber: value.length > 0 && filtered.length > 0 }));
    }

    if (field === 'enquirySourceNumber' && filterData.enquirySourceNumbers) {
      const filtered = filterData.enquirySourceNumbers.filter(num => num.toLowerCase().includes(value.toLowerCase()));
      setSuggestions(prev => ({ ...prev, enquirySourceNumber: filtered }));
      setShowSuggestions(prev => ({ ...prev, enquirySourceNumber: value.length > 0 && filtered.length > 0 }));
    }

    if (field === 'itemDetail' && filterData.itemDetails) {
      const filtered = filterData.itemDetails.filter(detail => detail.toLowerCase().includes(value.toLowerCase()));
      setSuggestions(prev => ({ ...prev, itemDetail: filtered }));
      setShowSuggestions(prev => ({ ...prev, itemDetail: value.length > 0 && filtered.length > 0 }));
    }

    if (field === 'quantity' && filterData.quantities) {
      const filtered = filterData.quantities.filter(qty => qty.toLowerCase().includes(value.toLowerCase()));
      setSuggestions(prev => ({ ...prev, quantity: filtered }));
      setShowSuggestions(prev => ({ ...prev, quantity: value.length > 0 && filtered.length > 0 }));
    }

    if (field === 'itemCode' && filterData.itemCodes) {
      const filtered = filterData.itemCodes.filter(code => code.toLowerCase().includes(value.toLowerCase()));
      setSuggestions(prev => ({ ...prev, itemCode: filtered }));
      setShowSuggestions(prev => ({ ...prev, itemCode: value.length > 0 && filtered.length > 0 }));
    }

    if (field === 'salesOrderNumber' && filterData.salesOrderNumbers) {
      const filtered = filterData.salesOrderNumbers.filter(num => num.toLowerCase().includes(value.toLowerCase()));
      setSuggestions(prev => ({ ...prev, salesOrderNumber: filtered }));
      setShowSuggestions(prev => ({ ...prev, salesOrderNumber: value.length > 0 && filtered.length > 0 }));
    }

    if (field === 'remarks' && filterData.remarks) {
      const filtered = filterData.remarks.filter(remark => remark.toLowerCase().includes(value.toLowerCase()));
      setSuggestions(prev => ({ ...prev, remarks: filtered }));
      setShowSuggestions(prev => ({ ...prev, remarks: value.length > 0 && filtered.length > 0 }));
    }

    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const fetchLocationsByDivision = async (division) => {
    try {
      const locations = await getAllLocationForDivision(division);
      setFilterData(prev => ({
        ...prev,
        locations: locations.map(loc => ({ LocationName: loc.Location, LocationCode: loc.LocationCode }))
      }));
    } catch (error) {
      console.error("Error fetching locations:", error);
    }
  };

  const validateFilters = () => {
    // No validation needed for export - allow export without any filters
    return true;
  };

  const getFilteredData = async () => {
    setLoading(true);
    try {
      return await getUserFrozenBids(context.userEmail, filters);
    } finally {
      setLoading(false);
    }
  };

  const setPopUpData = (data) => {
    if (data.name === 'Item Master') {
      handleInputChange('itemCode', data.result.ItemCode);
    } else if (data.name === 'Location Master' || data.name.startsWith('Location Master:')) {
      handleInputChange('location', data.result.Location);
    }
  };

  const showPopUp = (name, type, key, value) => {
    setPopUp({ name, type, key, value });
    setShowForm(true);
  };

  const handleExport = async () => {
    if (!validateFilters()) return;

    setLoading(true);
    try {
      const filteredData = await getFilteredData();

      if (!filteredData || filteredData.length === 0) {
        MySwal.fire({
          text: "No Records Found.",
          imageUrl: errorImage,
          customClass: "swal-success",
        });
        return;
      }

      const excelData = filteredData.map(item => ({
        EnquiryNumber: item.EnquiryNumber || '',
        EnquiryStatus: item.EnquiryStatus || '',
        EnquirySource: (item.EnquirySource === "Contract" ? "Contract" : "Non-Contract") || '',
        EnquirySourceNumber: item.EnquirySourceNumber || '',
        ItemCode: item.ItemCode || '',
        ItemDetail: item.ItemDetail || '',
        ItemType: item.ItemType || '',
        LogisticCompany: item.LogisticCompany || '',
        Division: item.Division || '',
        Location: item.Location || '',
        Quantity: item.Quantity || '',
        UOM: item.UOM || '',
        GST: item.GST || '',
        DueDate: item.DueDate ? formatDateInDDMMYYYY(item.DueDate) : '',
        PriceExclTaxes: item.CurrentBid || '',
        BidSubmissionDateTime: item.CurrentBidDate || '',
        RevisedDueDate: item.RevisedDueDate || '',
        SalesOrderNumber: item.SONumber || '',
        Remarks: item.Remarks || ''
      }));

      const colNames = [
        "Enquiry Number", "Enquiry Status", "Enquiry Source", "Enquiry Source Number",
        "Item Code", "Item Detail", "Item Type", "Logistic Company", "Division", "Location", "Quantity",
        "UOM", "GST%", "Due Date", "Price₹(Excl. taxes)", "Bid Submission Date Time",
        "Revised Due Date", "Sales Order Number", "Remarks"
      ];

      const fieldNames = [
        "EnquiryNumber", "EnquiryStatus", "EnquirySource", "EnquirySourceNumber",
        "ItemCode", "ItemDetail", "ItemType", "LogisticCompany", "Division", "Location", "Quantity",
        "UOM", "GST", "DueDate", "PriceExclTaxes", "BidSubmissionDateTime",
        "RevisedDueDate", "SalesOrderNumber", "Remarks"
      ];

      exportToExcel("Closed_Bids_Reports", "Reports", excelData, "A1:S1", colNames, fieldNames);

      // Add log entry for export action
      await addLogDetails(
        { Action: "Export", RecordCount: filteredData.length },
        "ClosedBids",
        "Export",
        "ExportLog",
        { userName: context.userName, userId: context.userId },
        "Reports"
      );

    } catch (error) {
      console.error("Export error:", error);
      MySwal.fire({
        text: "Failed to export data. Please try again.",
        imageUrl: errorImage,
        customClass: "swal-success",
      });
    } finally {
      setLoading(false);
    }
  };

  const resetFilters = () => {
    setFilters({
      enquiryNumber: '',
      enquiryStatus: [],
      enquirySource: '',
      enquirySourceNumber: '',
      itemCode: '',
      itemDetail: '',
      itemType: '',
      division: '',
      location: '',
      quantity: '',
      uom: '',
      gst: '',
      lnCompany: '',
      dueDate: '',
      price: '',
      bidSubmissionDate: '',
      revisedDueDate: '',
      salesOrderNumber: '',
      remarks: ''
    });
    setErrors({});
    setSuggestions({});
    setShowSuggestions({});
  };

  return (
    <>
      {loading && <div className="spinner-box"><Spinner animation="border" className="spinner" /></div>}
      <BreadcrumbComp />
      <Container fluid className="main-container">
        <div className="disposal-form">
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="g-input position-relative">
                  <Form.Control
                    id="enquiryNumber"
                    type="text"
                    name="enquiryNumber"
                    placeholder=" "
                    value={filters.enquiryNumber}
                    onChange={(e) => handleInputChange('enquiryNumber', e.target.value)}
                    onFocus={() => {
                      if (filters.enquiryNumber && filterData.enquiryNumbers) {
                        const filtered = filterData.enquiryNumbers.filter(num =>
                          num.toLowerCase().includes(filters.enquiryNumber.toLowerCase())
                        );
                        setSuggestions(prev => ({ ...prev, enquiryNumber: filtered }));
                        setShowSuggestions(prev => ({ ...prev, enquiryNumber: filtered.length > 0 }));
                      }
                    }}
                    onBlur={() => setTimeout(() => setShowSuggestions(prev => ({ ...prev, enquiryNumber: false })), 200)}
                    maxLength={30}
                  />
                  <Form.Label htmlFor="enquiryNumber">Enquiry Number</Form.Label>
                  {showSuggestions.enquiryNumber && (
                    <div className="position-absolute bg-white border rounded shadow-sm" style={{ zIndex: 1000, width: '100%', top: '100%' }}>
                      {suggestions.enquiryNumber?.map((suggestion, index) => (
                        <div
                          key={index}
                          className="p-2 cursor-pointer"
                          style={{ cursor: 'pointer' }}
                          onMouseDown={() => {
                            handleInputChange('enquiryNumber', suggestion);
                            setShowSuggestions(prev => ({ ...prev, enquiryNumber: false }));
                          }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#F9F2F6'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = '#FFFFFF'}
                        >
                          {suggestion}
                        </div>
                      ))}
                    </div>
                  )}
                </Form.Group>
              </Col>
              <Col md={6}>
                <div style={{ position: 'relative' }}>
                  <Multiselect
                    options={[{ key: 'Select All' }, ...filterData.enquiryStatuses.map(status => ({ key: status }))]}
                    selectedValues={filters.enquiryStatus.map(status => ({ key: status }))}
                    onSelect={(selectedList, selectedItem) => {
                      if (selectedItem.key === 'Select All') {
                        const allStatuses = filterData.enquiryStatuses;
                        setFilters(prev => ({ ...prev, enquiryStatus: allStatuses }));
                      } else {
                        const filteredList = selectedList.filter(item => item.key !== 'Select All');
                        setFilters(prev => ({ ...prev, enquiryStatus: filteredList.map(item => item.key) }));
                      }
                    }}
                    onRemove={(selectedList, removedItem) => {
                      if (removedItem.key === 'Select All') {
                        setFilters(prev => ({ ...prev, enquiryStatus: [] }));
                      } else {
                        const filteredList = selectedList.filter(item => item.key !== 'Select All');
                        setFilters(prev => ({ ...prev, enquiryStatus: filteredList.map(item => item.key) }));
                      }
                    }}
                    displayValue="key"
                    placeholder="Enquiry Status"
                    showCheckbox
                    className="custom-multiselect"
                  />
                  <BiSelectMultiple
                    size={20}
                    color="#707070"
                    style={{ position: 'absolute', right: '35px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}
                  />
                </div>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <SearchSelect
                  name="enquirySource"
                  label="Enquiry Source"
                  data={filterData.enquirySources}
                  value={filters.enquirySource}
                  onChange={(e) => handleInputChange('enquirySource', e.target.value)}
                  error={errors}
                  setError={setErrors}
                />
              </Col>
              <Col md={6}>
                <Form.Group className="g-input">
                  <Form.Control
                    id="enquirySourceNumber"
                    type="text"
                    name="enquirySourceNumber"
                    placeholder=" "
                    value={filters.enquirySourceNumber}
                    onChange={(e) => handleInputChange('enquirySourceNumber', e.target.value)}
                    maxLength={30}
                  />
                  <Form.Label htmlFor="enquirySourceNumber">Enquiry Source Number</Form.Label>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="g-input">
                  <div className="d-flex align-items-center">
                    <Form.Control
                      type="text"
                      value={filters.itemCode}
                      placeholder=" "
                      readOnly
                    />
                    <Form.Label>Item Code</Form.Label>
                    <BiSelectMultiple
                      size={20}
                      onClick={() => showPopUp('Item Master', 'Single', 'ItemCode', filters.itemCode)}
                      color="#707070"
                      style={{ marginLeft: "-2rem", cursor: "pointer" }}
                    />
                  </div>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="g-input position-relative">
                  <Form.Control
                    id="itemDetail"
                    type="text"
                    name="itemDetail"
                    placeholder=" "
                    value={filters.itemDetail}
                    onChange={(e) => handleInputChange('itemDetail', e.target.value)}
                    onFocus={() => {
                      if (filters.itemDetail && filterData.itemDetails) {
                        const filtered = filterData.itemDetails.filter(detail =>
                          detail.toLowerCase().includes(filters.itemDetail.toLowerCase())
                        );
                        setSuggestions(prev => ({ ...prev, itemDetail: filtered }));
                        setShowSuggestions(prev => ({ ...prev, itemDetail: filtered.length > 0 }));
                      }
                    }}
                    onBlur={() => setTimeout(() => setShowSuggestions(prev => ({ ...prev, itemDetail: false })), 200)}
                    maxLength={250}
                  />
                  <Form.Label htmlFor="itemDetail">Item Detail</Form.Label>
                  {showSuggestions.itemDetail && (
                    <div className="position-absolute bg-white border rounded shadow-sm" style={{ zIndex: 1000, width: '100%', top: '100%' }}>
                      {suggestions.itemDetail?.map((suggestion, index) => (
                        <div
                          key={index}
                          className="p-2 cursor-pointer"
                          style={{ cursor: 'pointer' }}
                          onMouseDown={() => {
                            handleInputChange('itemDetail', suggestion);
                            setShowSuggestions(prev => ({ ...prev, itemDetail: false }));
                          }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#F9F2F6'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = '#FFFFFF'}
                        >
                          {suggestion}
                        </div>
                      ))}
                    </div>
                  )}
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <SearchSelect
                  name="itemType"
                  label="Item Type"
                  data={filterData.itemTypes}
                  value={filters.itemType}
                  onChange={(e) => handleInputChange('itemType', e.target.value)}
                  error={errors}
                  setError={setErrors}
                />
              </Col>
              <Col md={6}>
                <SearchSelect
                  name="division"
                  label="Division "
                  data={filterData.divisions}
                  value={filters.division}
                  onChange={(e) => handleInputChange('division', e.target.value)}
                  error={errors}
                  setError={setErrors}
                />
              </Col>
            </Row>
            <br />
            <Row>
              <Col md={6}>
                <Form.Group className="g-input">
                  <div className="d-flex align-items-center">
                    <Form.Control
                      type="text"
                      value={filters.location}
                      placeholder=" "
                      readOnly
                    />
                    <Form.Label>Location</Form.Label>
                    <BiSelectMultiple
                      size={20}
                      onClick={() => {
                        if (filters.division) {
                          showPopUp(`Location Master:DIVISION:${filters.division}`, 'Single', 'Location', filters.location);
                        } else {
                          showPopUp('Location Master', 'Single', 'Location', filters.location);
                        }
                      }}
                      color="#707070"
                      style={{ marginLeft: "-2rem", cursor: "pointer" }}
                    />
                  </div>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="g-input position-relative">
                  <Form.Control
                    id="quantity"
                    type="text"
                    name="quantity"
                    placeholder=" "
                    value={filters.quantity}
                    onChange={(e) => handleInputChange('quantity', e.target.value)}
                    onFocus={() => {
                      if (filters.quantity && filterData.quantities) {
                        const filtered = filterData.quantities.filter(qty =>
                          qty.toString().includes(filters.quantity)
                        );
                        setSuggestions(prev => ({ ...prev, quantity: filtered }));
                        setShowSuggestions(prev => ({ ...prev, quantity: filtered.length > 0 }));
                      }
                    }}
                    onBlur={() => setTimeout(() => setShowSuggestions(prev => ({ ...prev, quantity: false })), 200)}
                    maxLength={50}
                  />
                  <Form.Label htmlFor="quantity">Quantity</Form.Label>
                  {showSuggestions.quantity && (
                    <div className="position-absolute bg-white border rounded shadow-sm" style={{ zIndex: 1000, width: '100%', top: '100%' }}>
                      {suggestions.quantity?.map((suggestion, index) => (
                        <div
                          key={index}
                          className="p-2 cursor-pointer"
                          style={{ cursor: 'pointer' }}
                          onMouseDown={() => {
                            handleInputChange('quantity', suggestion.toString());
                            setShowSuggestions(prev => ({ ...prev, quantity: false }));
                          }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#F9F2F6'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = '#FFFFFF'}
                        >
                          {suggestion}
                        </div>
                      ))}
                    </div>
                  )}
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <SearchSelect
                  name="uom"
                  label="UOM"
                  valueField="UOMCode"
                  labelField="UOMCode"
                  data={filterData.uoms}
                  value={filters.uom}
                  onChange={(e) => handleInputChange('uom', e.target.value)}
                  error={errors}
                  setError={setErrors}
                />
              </Col>
              <Col md={6}>
                <Form.Group className="g-input">
                  <Form.Control
                    id="gst"
                    type="text"
                    name="gst"
                    placeholder=" "
                    value={filters.gst}
                    onChange={(e) => handleInputChange('gst', e.target.value)}
                    maxLength={5}
                  />
                  <Form.Label htmlFor="gst">GST %</Form.Label>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="g-input">
                  <Form.Control
                    id="lnCompany"
                    type="text"
                    name="lnCompany"
                    placeholder=" "
                    value={filters.lnCompany}
                    onChange={(e) => handleInputChange('lnCompany', e.target.value)}
                    maxLength={4}
                  />
                  <Form.Label htmlFor="lnCompany">Logistic Company</Form.Label>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="g-input">
                  <Form.Control
                    id="dueDate"
                    type="date"
                    name="dueDate"
                    placeholder=" "
                    value={filters.dueDate}
                    onChange={(e) => handleInputChange('dueDate', e.target.value)}
                    max={new Date().toISOString().split('T')[0]}
                  />
                  <Form.Label htmlFor="dueDate">Due Date</Form.Label>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="g-input">
                  <Form.Control
                    id="price"
                    type="text"
                    name="price"
                    placeholder=" "
                    value={filters.price}
                    onChange={(e) => handleInputChange('price', e.target.value)}
                    maxLength={20}
                  />
                  <Form.Label htmlFor="price">Price</Form.Label>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="g-input">
                  <Form.Control
                    id="bidSubmissionDate"
                    type="date"
                    name="bidSubmissionDate"
                    placeholder=" "
                    value={filters.bidSubmissionDate}
                    onChange={(e) => handleInputChange('bidSubmissionDate', e.target.value)}
                    max={new Date().toISOString().split('T')[0]}
                  />
                  <Form.Label htmlFor="bidSubmissionDate">Bid Submission Date Time</Form.Label>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="g-input position-relative">
                  <Form.Control
                    id="salesOrderNumber"
                    type="text"
                    name="salesOrderNumber"
                    placeholder=" "
                    value={filters.salesOrderNumber}
                    onChange={(e) => handleInputChange('salesOrderNumber', e.target.value)}
                    onFocus={() => {
                      if (filters.salesOrderNumber && filterData.salesOrderNumbers) {
                        const filtered = filterData.salesOrderNumbers.filter(num =>
                          num.toLowerCase().includes(filters.salesOrderNumber.toLowerCase())
                        );
                        setSuggestions(prev => ({ ...prev, salesOrderNumber: filtered }));
                        setShowSuggestions(prev => ({ ...prev, salesOrderNumber: filtered.length > 0 }));
                      }
                    }}
                    onBlur={() => setTimeout(() => setShowSuggestions(prev => ({ ...prev, salesOrderNumber: false })), 200)}
                    maxLength={20}
                  />
                  <Form.Label htmlFor="salesOrderNumber">Sales Order Number</Form.Label>
                  {showSuggestions.salesOrderNumber && (
                    <div className="position-absolute bg-white border rounded shadow-sm" style={{ zIndex: 1000, width: '100%', top: '100%' }}>
                      {suggestions.salesOrderNumber?.map((suggestion, index) => (
                        <div
                          key={index}
                          className="p-2 cursor-pointer"
                          style={{ cursor: 'pointer' }}
                          onMouseDown={() => {
                            handleInputChange('salesOrderNumber', suggestion);
                            setShowSuggestions(prev => ({ ...prev, salesOrderNumber: false }));
                          }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#F9F2F6'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = '#FFFFFF'}
                        >
                          {suggestion}
                        </div>
                      ))}
                    </div>
                  )}
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={12} className="text-center">
                <Button
                  variant="success"
                  className="btn-export-color"
                  onClick={handleExport}
                  disabled={loading}
                >
                  {loading ? 'Exporting...' : 'Export to Excel'}
                </Button>
                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                <Button
                  variant="success"
                  className="btn-export-color me-2"
                  onClick={resetFilters}
                  disabled={loading}
                >
                  Reset Filters
                </Button>
              </Col>
            </Row>
          </Form>
        </div>

        <RenderIf isTrue={popUp.type === 'Single' && showForm}>
          <SingleValuePopUp
            show={showForm}
            onHide={() => setShowForm(false)}
            setSelected={setPopUpData}
            mastername={popUp.name}
            paramkey={popUp.key}
            paramvalue={popUp.value}
            backdrop="static"
          />
        </RenderIf>
      </Container>
    </>
  );
};

export default ClosedBids;